import { useContext, useEffect, useMemo, useState } from "react";
import { BreadTransaction, P, Predicate } from "breadcommon";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsNavContext } from "./TransactionsScreen";
import { buildClasses } from "../utils/buildClasses";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";

export function TransactionColumnFilterType({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const transactions = useContext(TransactionsContext);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedSubtypes, setSelectedSubtypes] = useState<string[]>([]);

  useEffect(() => {
    if (!hasActiveFilter) {
      setSelectedTypes([]);
      setSelectedSubtypes([]);
    }
  }, [hasActiveFilter]);

  // Extract unique type and subtype values from all transactions
  const uniqueTypes = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(transactions.values())
            .map((transaction) => transaction.type)
            .filter((ttype): ttype is string => ttype !== null),
        ),
      ).sort(),
    [transactions],
  );

  const uniqueSubtypes = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(transactions.values())
            .map((transaction) => transaction.subtype)
            .filter((subtype): subtype is string => subtype !== null),
        ),
      ).sort(),
    [transactions],
  );

  function createTypePredicate(
    types: string[],
    subtypes: string[],
  ): Predicate | null {
    const predicates: Predicate[] = [];

    if (types.length > 0) {
      const typeField = P.Field<BreadTransaction, "type">("type");
      const typePredicates = types.map((type) =>
        P.Equals(typeField, P.Literal(type)),
      );
      predicates.push(P.Any(...typePredicates));
    }

    if (subtypes.length > 0) {
      const subtypeField = P.Field<BreadTransaction, "subtype">("subtype");
      const subtypePredicates = subtypes.map((subtype) =>
        P.Equals(subtypeField, P.Literal(subtype)),
      );
      predicates.push(P.Any(...subtypePredicates));
    }

    if (predicates.length === 0) {
      return null;
    }

    return predicates.length === 1 ? predicates[0] : P.All(...predicates);
  }

  function applyFilter(types: string[], subtypes: string[]) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.TYPE];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.TYPE]: {
          ...currentSettings,
          predicate: createTypePredicate(types, subtypes),
        },
      },
    }));
  }

  function handleTypeSelectionChange(types: string[]) {
    setSelectedTypes(types);
    applyFilter(types, selectedSubtypes);
  }

  function handleSubtypeSelectionChange(subtypes: string[]) {
    setSelectedSubtypes(subtypes);
    applyFilter(selectedTypes, subtypes);
  }

  if (!isOpen) {
    return null;
  }

  // Prepare type entries
  const typeEntries = uniqueTypes.map((type) => ({
    id: type,
    text: type,
    icon: null,
  }));

  // Prepare subtype entries
  const subtypeEntries = uniqueSubtypes.map((subtype) => ({
    id: subtype,
    text: subtype,
    icon: null,
  }));

  return (
    <div className={buildClasses("flex", "flex-col", "gap-2")}>
      <div className={buildClasses("flex", "gap-2")}>
        <RyeDropDownMultiSelect
          selectedEntryIds={selectedTypes}
          entries={typeEntries}
          onSelectionChange={handleTypeSelectionChange}
          onEsc={onClose}
          searchable
          placeholder="Search types..."
          size="md"
          width="md"
          variant="outlined"
        />

        <RyeDropDownMultiSelect
          selectedEntryIds={selectedSubtypes}
          entries={subtypeEntries}
          onSelectionChange={handleSubtypeSelectionChange}
          onEsc={onClose}
          searchable
          placeholder="Search subtypes..."
          size="md"
          width="md"
          variant="outlined"
        />
      </div>
    </div>
  );
}
